import type { CartStore } from "@/store/cart/cart-store"
import type { CartLine } from "@/store/cart/cart.types"
import { useCartStore } from "@/store/cart/cart-store"
import { getIsolatedRestaurantId, selectSafeCartItems } from "@/store/cart/cart-tenant-isolation"
import { sumMenuItemQuantity } from "@/domain/cart/cart.validation"

/** Isolated tenant cart, or in-memory items before isolation (tests / pre-gate). */
export const selectCartItems = (state: CartStore) => {
  const isolated = getIsolatedRestaurantId()
  if (!isolated) return state.items
  return selectSafeCartItems(state.items, isolated)
}

export function readSafeCartItems(): CartLine[] {
  return selectCartItems(useCartStore.getState())
}

export const selectCartLockFlags = (state: CartStore) => ({
  isLocked: state.isLocked,
  isCheckoutLocked: state.isCheckoutLocked,
})

/** Aggregate quantity across all configs for one menu_item_id (read-only). */
export function selectCartQuantityByMenuItemId(
  items: CartLine[],
  menuItemId: string
): number {
  return sumMenuItemQuantity(items, menuItemId)
}

/**
 * Per-card quantity hook. Selector returns a primitive number so Zustand
 * skips re-render when other cart lines change but this item's total is unchanged.
 */
export function useCartQuantityByMenuItemId(menuItemId: string): number {
  const id = String(menuItemId ?? "").trim()
  return useCartStore((state) =>
    selectCartQuantityByMenuItemId(selectCartItems(state), id)
  )
}
